import * as Yup from "yup";
import {
  FileObject,
  FileStatus
} from "../../components/formHelpers/FormFileUpload";
import { KYCFileCollection } from "../../services/KYCDocumentsService/KYCDocumentsService.type";

export const KYCContents = {
  titles: {
    loanApplicationForm: "Loan Application Form",
    loanDetailSheet: "Loan Details Sheet",
    postDatedCheck: "Post-dated Cheque",
    POI: "Individual Identity Proof",
    POA: "Individual Address Proof",
    POB: "Proof of Business",
    companyPAN: "Company PAN",
    partnershipDeed: "Partnership Deed",
    PAL: "Partnership Authority Letter",
    MOA: "MOA/AOA",
    resolution: "Board Resolution",
    directors: "List Of Directors",
    shareholders: "List Of Shareholders",
    nachFileScan: "Nach File Scan",
  },
  labels: {
    loanApplicationForm: "Upload Loan Application Form",
    loanDetailSheet: "Upload Loan Details Sheet",
    postDatedCheck: "Upload Post-dated Cheque",
    POIDropdown:
      "Select Identity Proof (any one of the documents below & upload)",
    POADropdown:
      "Select Address Proof (any one of the documents below & upload)",
    POBDropdown: "Select Business Proof (minimum 2 of the following documents)",
    POI: "Upload Identity Proof",
    POA: "Upload Address Proof",
    POB: "Upload Business Proof",
    companyPAN: "Upload Company PAN",
    partnershipDeed: "Upload Partnership Deed",
    PAL: "Upload Authority Letter",
    MOA: "Upload MOA/AOA",
    resolution: "Upload Board Resolution",
    directors: "Upload List Of Directors",
    shareholders: "Upload List Of Shareholders",
    nachFileScan: "Upload Nach File Scan",
  },
  placeholders: {
    POI: "Search by Identity Proof",
    POA: "Search by Address Proof",
    POB: "Search by Business Proof"
  },
  buttons: {
    submit: "Submit"
  },
  feedbacks: {
    DMISubmitted: "Documents are submitted for verification",
    POBTooltip: `1.Registration certificate.
    2.Certificate/licence issued by the municipal authorities under Shop and Establishment Act.
    3.(provisional/final). 24CST/VAT/ GST certificate.
    4.Certificate/registration document issued by Sales Tax/Service Tax/Professional Tax authorities.
    5.IEC (Importer Exporter Code) issued to the proprietary concern by the office of DGFT/ Licence/certificate of practice issued in the name of the proprietary concern by any professional body incorporated under a statute.
    6.Utility bills such as electricity, water, and landline telephone bills.
    `,
    downloadTooltip: "Download the pre-filled editable form, scan and upload",
    inactiveGSTIN: (gstin: string) =>
      `Your GSTIN ${gstin} is showing up as inactive. Please reach out to the GST authority for the same. Once activated, please re-submit.`
  }
};

export const KYC_SUPPORTED_EXTENSIONS = [".png", ".jpeg", ".jpg", ".pdf"];

const errorMessages = {
  minOne: "Atleast one file is mandatory",
  minTwo: "Atleast two files are mandatory",
  differentSubTypes: "Atleast two different document types are mandatory",
  required: "Required",
  processing: "One of the file is processing"
};

const validateFiles = (minDocs: number, error: string) => {
  return Yup.array<FileObject>()
    .min(minDocs, error)
    .compact(
      (item: FileObject) =>
        item.removed ||
        item.status === (FileStatus.Invalid || FileStatus.Failed)
    )
    .test({
      name: "subDocType_validator",
      test(value) {
        if (this.path === "POB" && value?.length) {
          const numberOfSubDocTypes = new Set(
            value.map((item: FileObject) => item.subDocType)
          ).size;
          if (numberOfSubDocTypes < 2) {
            return this.createError({
              message: errorMessages.differentSubTypes
            });
          }
        }
        return true;
      }
    })
    .of(
      Yup.object<FileObject>().shape<FileObject>({
        id: Yup.string().required(),
        name: Yup.string().required(),
        removed: Yup.boolean().oneOf([false]),
        status: Yup.mixed().test({
          name: "status_validator",
          test(value) {
            if (value === FileStatus.Processing) {
              return this.createError({
                message: errorMessages.processing
              });
            } else {
              return true;
            }
          }
        })
      })
    );
};
export const KYCValidationSchema = Yup.object().shape<
  Partial<KYCFileCollection>
>({
  "Loan Application Form": validateFiles(1, errorMessages.required),
  "Loan Detail Sheet": validateFiles(1, errorMessages.required),
  "Post-dated Cheque": validateFiles(1, errorMessages.required),
  POI: validateFiles(1, errorMessages.minOne),
  POA: validateFiles(1, errorMessages.minOne),
  POB: validateFiles(2, errorMessages.minTwo),
  "Company PAN": validateFiles(1, errorMessages.required),
  "Partnership Deed": validateFiles(1, errorMessages.required),
  "Partnership Authority Letter": validateFiles(1, errorMessages.required),
  "MOA/AOA": validateFiles(1, errorMessages.required),
  "Board Resolution": validateFiles(1, errorMessages.required),
  "List of Director": validateFiles(1, errorMessages.required),
  "List of Shareholder": validateFiles(1, errorMessages.required)
});
